/**
 * EnquiriesAnalytics — Stage 2 dashboard for managers.
 *
 * Sections:
 *   - Overview KPIs (created / resolved / conversion / response / resolution)
 *   - Daily trend (created vs resolved vs booked)
 *   - Funnel (bar of status distribution)
 *   - Per-owner workload (table with sparkbar for overdue)
 *   - Per-category conversion (table)
 *
 * Timeframe selector: 7 / 30 / 90 days.
 */
import { fmtDate, isoDate as melDate, todayISO as melToday } from "@/lib/intl";
import { DateField } from "@/components/ui/date-field";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  BarChart3, TrendingUp, Users as UsersIcon, Tag, Clock, ArrowLeft, Loader2,
  Percent, CheckCircle2, AlertCircle,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Legend,
} from "recharts";
import { ConversionFunnelCard } from "@/components/enquiries/analytics/ConversionFunnelCard";
import { LostReasonsCard } from "@/components/enquiries/analytics/LostReasonsCard";


const RANGES = [
  { key: 7,  label: "7 days" },
  { key: 30, label: "30 days" },
  { key: 90, label: "90 days" },
];


function fmtSeconds(secs) {
  if (secs == null) return "—";
  if (secs < 60) return `${Math.round(secs)}s`;
  const mins = secs / 60;
  if (mins < 60) return `${mins.toFixed(1)} min`;
  const hrs = mins / 60;
  if (hrs < 48) return `${hrs.toFixed(1)} h`;
  return `${(hrs / 24).toFixed(1)} d`;
}

function fmtPct(x) {
  if (x == null) return "—";
  return `${(x * 100).toFixed(0)}%`;
}

function isoDaysAgo(days, anchorIso) {
  const d = anchorIso ? new Date(anchorIso + "T00:00:00Z") : new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return melDate(d);
}

const STATUS_LABELS = {
  new: "New",
  assigned: "Assigned",
  in_progress: "In progress",
  waiting_client: "Waiting · client",
  waiting_internal: "Waiting · internal",
  scheduled: "Scheduled",
  resolved: "Resolved",
  closed_no_action: "Closed · no action",
};

const STATUS_COLOURS = {
  new: "bg-indigo-100 text-indigo-800 border-indigo-200",
  assigned: "bg-sky-100 text-sky-800 border-sky-200",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200",
  waiting_client: "bg-yellow-100 text-yellow-800 border-yellow-200",
  waiting_internal: "bg-orange-100 text-orange-800 border-orange-200",
  scheduled: "bg-violet-100 text-violet-800 border-violet-200",
  resolved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  closed_no_action: "bg-zinc-100 text-zinc-700 border-zinc-200",
};


export default function EnquiriesAnalytics() {
  const [days, setDays] = useState(30);
  const [anchorDate, setAnchorDate] = useState("");  // "" = today
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [byOwner, setByOwner] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [daily, setDaily] = useState([]);
  const [loading, setLoading] = useState(true);

  const todayIso = useMemo(() => melToday(), []);
  const effectiveAnchor = anchorDate || todayIso;

  const params = useMemo(() => {
    const p = { start: isoDaysAgo(days, anchorDate || null) };
    if (anchorDate) {
      // Backend treats `end` as exclusive upper bound → add 1 day so the
      // anchor date itself is included in the trailing window.
      const next = new Date(anchorDate + "T00:00:00Z");
      next.setUTCDate(next.getUTCDate() + 1);
      p.end = melDate(next);
    }
    return p;
  }, [days, anchorDate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rOv, rFn, rOw, rCat, rDaily] = await Promise.all([
        api.get("/enquiries/analytics/overview",   { params }),
        api.get("/enquiries/analytics/funnel",     { params }),
        api.get("/enquiries/analytics/by-owner",   { params }),
        api.get("/enquiries/analytics/by-category",{ params }),
        api.get("/enquiries/analytics/daily",      { params }),
      ]);
      setOverview(rOv.data);
      setFunnel(rFn.data || []);
      setByOwner(rOw.data || []);
      setByCategory(rCat.data || []);
      setDaily(rDaily.data || []);
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const dailyPretty = useMemo(
    () => daily.map((d) => ({ ...d, label: d.day.slice(5) })),
    [daily],
  );

  return (
    <div className="max-w-[1400px] mx-auto p-4 space-y-4" data-testid="enquiries-analytics-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Link to="/enquiries" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="analytics-back">
            <ArrowLeft className="h-3 w-3" /> Back to enquiries
          </Link>
          <h1 className="text-2xl font-semibold text-zinc-950 flex items-center gap-2 mt-1">
            <BarChart3 className="h-6 w-6" /> Enquiries analytics
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Conversion, response time and per-owner workload so you can spot bottlenecks.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex rounded-md border border-zinc-300 overflow-hidden">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setDays(r.key)}
                className={`px-3 h-8 text-xs ${days === r.key ? "bg-zinc-950 text-white" : "bg-white text-zinc-700"} ${r.key !== RANGES[0].key ? "border-l border-zinc-300" : ""}`}
                data-testid={`analytics-range-${r.key}`}
              >{r.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-1.5" data-testid="analytics-anchor-row">
            <label className="text-2xs uppercase tracking-wider text-zinc-500">Anchor</label>
            <DateField
              type="date"
              value={anchorDate}
              max={todayIso}
              onChange={(e) => setAnchorDate(e.target.value)}
              className="h-8 w-36 border-zinc-300 rounded px-2 text-xs md:text-xs font-mono-num bg-white"
              data-testid="analytics-anchor-date"
              title={`Freeze the trailing ${days}-day window to end at this date`}
            />
            {anchorDate && (
              <button
                onClick={() => setAnchorDate("")}
                className="h-8 px-2 text-2xs border border-zinc-300 rounded bg-white hover:bg-zinc-50"
                data-testid="analytics-anchor-clear"
              >Clear</button>
            )}
          </div>
        </div>
      </div>

      <div className="text-2xs text-zinc-500 -mt-2" data-testid="analytics-window-summary">
        Trailing {days} days ending{" "}
        <span className="font-medium text-zinc-700">
          {anchorDate ? fmtDate(anchorDate) : "today"}
        </span>
        {" "}· window {fmtDate(isoDaysAgo(days, anchorDate || null))} → {fmtDate(effectiveAnchor)}
      </div>

      {loading && !overview ? (
        <div className="p-6 text-sm text-zinc-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-2" data-testid="analytics-kpis">
            <Kpi label="Created" value={overview?.total_created} icon={TrendingUp} testId="kpi-created" />
            <Kpi label="Resolved" value={overview?.resolved} icon={CheckCircle2} testId="kpi-resolved"
              hint={overview?.total_created ? `of ${overview.total_created} created in window` : undefined} />
            <Kpi label="Booked / resolved" value={fmtPct(overview?.conversion_rate)} tone={overview?.conversion_rate > 0.4 ? "green" : overview?.conversion_rate < 0.15 ? "red" : "zinc"} icon={Percent} testId="kpi-conversion" hint={`${overview?.booked || 0} booked / ${overview?.resolved || 0} resolved`} />
            <Kpi label="Median response" value={fmtSeconds(overview?.median_response_seconds)} icon={Clock} testId="kpi-response" />
            <Kpi label="Median resolution" value={fmtSeconds(overview?.median_resolution_seconds)} icon={Clock} testId="kpi-resolution" />
            <Kpi label="Overdue open" value={overview?.overdue_open} tone={overview?.overdue_open > 0 ? "red" : "zinc"} icon={AlertCircle} testId="kpi-overdue" />
          </div>

          <ConversionFunnelCard />
          <LostReasonsCard start={params.start} end={params.end} />

          {/* Daily trend */}
          <Card className="p-4 rounded-md shadow-none border border-zinc-200" data-testid="analytics-daily-chart">
            <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
              <TrendingUp className="h-3.5 w-3.5" /> Daily trend
            </div>
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyPretty}>
                  <CartesianGrid stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveEnd" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={30} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="created"  stroke="#2563eb" dot={false} strokeWidth={2} name="Created" />
                  <Line type="monotone" dataKey="resolved" stroke="#059669" dot={false} strokeWidth={2} name="Resolved" />
                  <Line type="monotone" dataKey="booked"   stroke="#a855f7" dot={false} strokeWidth={2} name="Booked" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Status tally — explicit counts + percentages */}
          <Card className="p-4 rounded-md shadow-none border border-zinc-200" data-testid="analytics-status-tally">
            <div className="flex items-center justify-between mb-2">
              <div className="text-2xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
                <BarChart3 className="h-3.5 w-3.5" /> Status tally
                <span className="normal-case tracking-normal text-zinc-400 ml-1">
                  · current status of enquiries created in this window
                </span>
              </div>
              <div className="text-2xs text-zinc-500">
                {(() => {
                  const total = funnel.reduce((a, f) => a + (f.count || 0), 0);
                  return <>Total enquiries in window: <span className="font-semibold text-zinc-900 font-mono-num" data-testid="analytics-total-enquiries">{total}</span></>;
                })()}
              </div>
            </div>
            {funnel.every((f) => (f.count || 0) === 0) ? (
              <div className="text-xs text-zinc-500 italic">No enquiries created in this window.</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-1.5">
                {funnel.map((f) => {
                  const total = funnel.reduce((a, x) => a + (x.count || 0), 0);
                  const pct = total ? Math.round(((f.count || 0) / total) * 100) : 0;
                  return (
                    <div
                      key={f.stage}
                      className={`border rounded px-2 py-1.5 ${STATUS_COLOURS[f.stage] || "bg-zinc-50 border-zinc-200 text-zinc-700"}`}
                      data-testid={`analytics-status-${f.stage}`}
                    >
                      <div className="text-2xs uppercase tracking-wider opacity-80">
                        {STATUS_LABELS[f.stage] || f.stage}
                      </div>
                      <div className="flex items-baseline gap-1">
                        <div className="text-lg font-semibold font-mono-num">{f.count || 0}</div>
                        <div className="text-2xs opacity-70">{pct}%</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Funnel */}
            <Card className="p-4 rounded-md shadow-none border border-zinc-200" data-testid="analytics-funnel-chart">
              <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
                <BarChart3 className="h-3.5 w-3.5" /> Status distribution (created in window)
              </div>
              <div className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={funnel} layout="vertical" margin={{ left: 30 }}>
                    <CartesianGrid stroke="#f1f5f9" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="stage" tick={{ fontSize: 10 }} width={110}
                           tickFormatter={(v) => v.replace("_", " ")} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Bar dataKey="count" fill="#0f172a" radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* Per-category conversion */}
            <Card className="p-4 rounded-md shadow-none border border-zinc-200" data-testid="analytics-category-table">
              <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
                <Tag className="h-3.5 w-3.5" /> Category conversion
              </div>
              {byCategory.length === 0 ? (
                <div className="p-4 text-xs text-zinc-500 italic">No enquiries in this window.</div>
              ) : (
                <div className="max-h-[220px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="text-2xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                      <tr>
                        <th className="text-left py-1.5">Category</th>
                        <th className="text-right py-1.5">Total</th>
                        <th className="text-right py-1.5">Resolved</th>
                        <th className="text-right py-1.5" title="Resolved ÷ Total — how many of this category's enquiries reached a resolved status">Resolved %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {byCategory.map((c) => {
                        const rate = c.resolution_rate ?? c.conversion_rate;
                        return (
                          <tr key={c.category} data-testid={`analytics-category-${c.category}`}>
                            <td className="py-1.5">
                              <span className="text-zinc-900">{c.category}</span>
                              {c.group && <span className="text-zinc-400 ml-1">· {c.group}</span>}
                            </td>
                            <td className="text-right font-mono-num text-zinc-800">{c.total}</td>
                            <td className="text-right font-mono-num text-zinc-800">{c.resolved}</td>
                            <td className={`text-right font-mono-num ${rate > 0.4 ? "text-emerald-700" : rate < 0.15 && rate != null ? "text-red-700" : "text-zinc-800"}`}>
                              {fmtPct(rate)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>

          {/* Per-owner workload */}
          <Card className="p-4 rounded-md shadow-none border border-zinc-200" data-testid="analytics-owner-table">
            <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
              <UsersIcon className="h-3.5 w-3.5" /> Per-owner workload
            </div>
            {byOwner.length === 0 ? (
              <div className="p-4 text-xs text-zinc-500 italic">Nobody has been assigned an enquiry yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-2xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                    <tr>
                      <th className="text-left py-1.5">Owner</th>
                      <th className="text-right py-1.5">Open</th>
                      <th className="text-right py-1.5">Overdue</th>
                      <th className="text-right py-1.5">Resolved</th>
                      <th className="text-right py-1.5">Booked</th>
                      <th className="text-right py-1.5">Conv.</th>
                      <th className="text-right py-1.5">Median response</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {byOwner.map((o) => (
                      <tr key={o.user_id} data-testid={`analytics-owner-${o.user_id}`}>
                        <td className="py-1.5 text-zinc-900">{o.name}</td>
                        <td className="text-right font-mono-num text-zinc-800">{o.open}</td>
                        <td className={`text-right font-mono-num ${o.overdue > 0 ? "text-red-700 font-semibold" : "text-zinc-800"}`}>{o.overdue}</td>
                        <td className="text-right font-mono-num text-zinc-800">{o.resolved}</td>
                        <td className="text-right font-mono-num text-zinc-800">{o.booked}</td>
                        <td className={`text-right font-mono-num ${o.conversion_rate > 0.4 ? "text-emerald-700" : o.conversion_rate < 0.15 && o.conversion_rate != null ? "text-red-700" : "text-zinc-800"}`}>
                          {fmtPct(o.conversion_rate)}
                        </td>
                        <td className="text-right font-mono-num text-zinc-800">{fmtSeconds(o.median_response_seconds)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}


function Kpi({ label, value, icon: Icon, tone = "zinc", testId, hint }) {
  const bg = {
    zinc: "border-zinc-200 bg-white",
    green: "border-emerald-200 bg-emerald-50",
    red: "border-red-200 bg-red-50",
  }[tone] || "border-zinc-200 bg-white";
  return (
    <div className={`p-3 rounded-md border ${bg}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-2xs uppercase tracking-wider text-zinc-500">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className="text-2xl font-semibold text-zinc-950 font-mono-num mt-1">
        {value ?? "—"}
      </div>
      {hint && <div className="text-2xs text-zinc-500 mt-0.5">{hint}</div>}
    </div>
  );
}
